import React from 'react';
import { WorkflowViewProvider } from './contexts/WorkflowViewContext';
import { ProductTourProvider } from './contexts/ProductTourContext';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { AuthProvider } from './contexts/AuthContext';
import { ChatSupportProvider } from './contexts/ChatSupportContext';
import { QuickStartProvider, useQuickStart } from './contexts/QuickStartContext';
import { QuickStartAssistantProvider } from './contexts/QuickStartAssistantContext';
import QuickStartModal from './components/quickstart/QuickStartModal';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ToastProvider } from './contexts/ToastContext';
import { BackgroundReportProvider } from './contexts/BackgroundReportContext';
import ToastContainer from './components/ToastContainer';
import { ActivityTracker } from './components/ActivityTracker';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import SafeRoute from './components/SafeRoute';
import Header from './components/Header';
import AdminLayout from './components/AdminLayout';
import ImpersonationBanner from './components/ImpersonationBanner';
import FloatingSupportChat from './components/FloatingSupportChat';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import SearchPalette from './components/SearchPalette';
import Home from './pages/Home';
import Support from './pages/Support';
import Admin from './pages/Admin';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import SystemAdmin from './pages/SystemAdmin';
import AuditDashboard from './pages/AuditDashboard';
import ChangePassword from './pages/ChangePassword';
import Settings from './pages/Settings';
import WorkflowReport from './pages/WorkflowReport';
import Comparison from './pages/Comparison';
import PromptTemplates from './pages/PromptTemplates';
import KnowledgeBase from './pages/KnowledgeBase';
import Insights from './pages/Insights';
import AgentActivity from './pages/AgentActivity';
import AgentApprove from './pages/AgentApprove';
import AgentVerifyAlias from './pages/AgentVerifyAlias';
import CompanyBilling from './pages/CompanyBilling';
import PdfExportPreview from './pages/PdfExportPreview';
import PdfExportPreviewV2 from './components/pdf-export-v2/PdfExportPreviewV2';
import ProposalEditor from './components/ProposalEditor';
import MobileApp from './mobile/MobileApp';
import MobileRedirectGuard from './mobile/MobileRedirectGuard';
// Import global error handlers to initialize them
import './utils/globalErrorHandlers';

// Custom SafeRoute for WorkflowReport that accepts both state data and URL parameters
const WorkflowReportSafeRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  // Check if we have state data
  const hasStateData = !!location.state;
  
  // Check if we have sessionStorage data
  let hasSessionData = false;
  try {
    hasSessionData = !!sessionStorage.getItem('workflow-report-data');
  } catch (e) {
    console.error('Failed to check sessionStorage:', e);
  }
  
  // Check if we have valid URL parameters that WorkflowReport can use
  const urlParams = new URLSearchParams(location.search);
  const hasValidUrlParams = !!(
    urlParams.get('reportId') || 
    urlParams.get('sessionId') || 
    urlParams.get('data')
  );
  
  // Allow access if any of these conditions are met
  const hasValidData = hasStateData || hasSessionData || hasValidUrlParams;
  
  if (hasValidData) {
    return (
      <SafeRoute 
        requiresData={false} // Don't require state data since we have other valid data
        sessionStorageKey="workflow-report-data"
      >
        {children}
      </SafeRoute>
    );
  }
  
  // Fall back to standard SafeRoute behavior if no valid data found
  return (
    <SafeRoute 
      requiresData={true} 
      sessionStorageKey="workflow-report-data"
      timeout={3000}
      onDataMissing={() => {
        console.warn('WorkflowReport: No valid data found - no state, sessionStorage, or URL parameters');
      }}
    >
      {children}
    </SafeRoute>
  );
};

// Reads QuickStartContext and renders the modal — a hook can't be called
// directly inside JSX, so this small wrapper is how the provider's state
// reaches the actual <QuickStartModal /> render.
const QuickStartModalHost: React.FC = () => {
  const { isOpen, closeQuickStart } = useQuickStart();
  return <QuickStartModal isOpen={isOpen} onClose={closeQuickStart} />;
};

function App() {
  // Detect if we're on the admin subdomain
  const isAdminDomain = window.location.hostname.startsWith('admin.');

  return (
    <ErrorBoundary onError={(error, errorInfo) => {
      console.error('Global App Error:', error, errorInfo);
      // Here you could send to error tracking service
    }}>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <BackgroundReportProvider>
              <ImpersonationProvider>
                <ActivityTracker>
                  <DocumentProvider>
                    <Router>
                      <WorkflowViewProvider>
                      <ProductTourProvider>
                      <ChatSupportProvider>
                      <QuickStartProvider>
                      <QuickStartAssistantProvider>
                        <div className="relative flex h-screen w-screen flex-col bg-slate-50 dark:bg-dark-bg font-sans transition-colors duration-200">
                      {/* Impersonation Banner - Always visible when impersonating */}
                      <ImpersonationBanner />
                      {!isAdminDomain && <MobileRedirectGuard />}

                      <Routes>
                      {/* Public route - Login */}
                      <Route path="/login" element={
                        <SafeRoute>
                          <Login />
                        </SafeRoute>
                      } />
                      <Route path="/reset-password" element={
                        <SafeRoute>
                          <ResetPassword />
                        </SafeRoute>
                      } />
                      {/* Public token pages from Agent Mailbox emails (approve-by-link, alias verification) */}
                      <Route path="/agent/approve" element={
                        <SafeRoute>
                          <AgentApprove />
                        </SafeRoute>
                      } />
                      <Route path="/agent/verify-alias" element={
                        <SafeRoute>
                          <AgentVerifyAlias />
                        </SafeRoute>
                      } />

                      {/* Mobile experience — protected, no desktop chrome */}
                      {!isAdminDomain && (
                        <Route path="/m/*" element={
                          <ProtectedRoute>
                            <MobileApp />
                          </ProtectedRoute>
                        } />
                      )}

                      {/* Protected routes - Different layout based on subdomain */}
                      {isAdminDomain ? (
                        // Admin Portal Layout
                        <Route path="/*" element={
                          <ProtectedRoute>
                            <AdminLayout>
                              <Routes>
                                <Route path="/sysadmin" element={
                                  <SafeRoute>
                                    <SystemAdmin />
                                  </SafeRoute>
                                } />
                                <Route path="/sysadmin/dashboard" element={
                                  <SafeRoute>
                                    <AuditDashboard />
                                  </SafeRoute>
                                } />
                                <Route path="/change-password" element={
                                  <SafeRoute>
                                    <ChangePassword />
                                  </SafeRoute>
                                } />
                                <Route path="/settings" element={
                                  <SafeRoute>
                                    <Settings />
                                  </SafeRoute>
                                } />
                                {/* Default route for admin portal */}
                                <Route path="/*" element={
                                  <SafeRoute>
                                    <SystemAdmin />
                                  </SafeRoute>
                                } />
                              </Routes>
                            </AdminLayout>
                          </ProtectedRoute>
                        } />
                      ) : (
                        // Main App Layout
                        <Route path="/*" element={
                          <ProtectedRoute>
                            <div className="flex h-full w-full flex-col">
                              <SafeRoute>
                                <Header />
                              </SafeRoute>
                              <div className="flex flex-1">
                                <Routes>
                                  <Route path="/" element={
                                    <SafeRoute>
                                      <Home />
                                    </SafeRoute>
                                  } />
                                  <Route path="/customers/:customerId" element={
                                    <SafeRoute>
                                      <Home />
                                    </SafeRoute>
                                  } />
                                  <Route path="/customers/:customerId/projects/:projectId" element={
                                    <SafeRoute>
                                      <Home />
                                    </SafeRoute>
                                  } />
                                  <Route path="/support" element={
                                    <SafeRoute>
                                      <Support />
                                    </SafeRoute>
                                  } />
                                  <Route path="/admin" element={
                                    <SafeRoute>
                                      <Admin />
                                    </SafeRoute>
                                  } />
                                  <Route path="/admin/billing" element={
                                    <SafeRoute>
                                      <CompanyBilling />
                                    </SafeRoute>
                                  } />
                                  <Route path="/sysadmin" element={
                                    <SafeRoute>
                                      <SystemAdmin />
                                    </SafeRoute>
                                  } />
                                  <Route path="/sysadmin/dashboard" element={
                                    <SafeRoute>
                                      <AuditDashboard />
                                    </SafeRoute>
                                  } />
                                  <Route path="/change-password" element={
                                    <SafeRoute>
                                      <ChangePassword />
                                    </SafeRoute>
                                  } />
                                  <Route path="/settings" element={
                                    <SafeRoute>
                                      <Settings />
                                    </SafeRoute>
                                  } />
                                  <Route path="/workflow-report" element={
                                    <WorkflowReportSafeRoute>
                                      <WorkflowReport />
                                    </WorkflowReportSafeRoute>
                                  } />
                                  <Route path="/pdf-export-preview" element={
                                    <PdfExportPreview />
                                  } />
                                  <Route path="/pdf-export-preview-v2" element={
                                    <PdfExportPreviewV2 />
                                  } />
                                  <Route path="/proposal-editor/:id" element={
                                    <SafeRoute>
                                      <ProposalEditor />
                                    </SafeRoute>
                                  } />
                                  <Route path="/comparison" element={
                                    <SafeRoute>
                                      <Comparison />
                                    </SafeRoute>
                                  } />
                                  <Route path="/prompt-templates" element={
                                    <SafeRoute>
                                      <PromptTemplates />
                                    </SafeRoute>
                                  } />
                                  <Route path="/knowledge-base" element={
                                    <SafeRoute>
                                      <KnowledgeBase />
                                    </SafeRoute>
                                  } />
                                  <Route path="/insights" element={
                                    <SafeRoute>
                                      <Insights />
                                    </SafeRoute>
                                  } />
                                  <Route path="/agent" element={
                                    <SafeRoute>
                                      <AgentActivity />
                                    </SafeRoute>
                                  } />
                                </Routes>
                              </div>
                            </div>
                          </ProtectedRoute>
                        } />
                      )}
                    </Routes>
                    {/* Floating support chat - rendered at root level, persists across navigation.
                        Hidden on the mobile experience where it would fight the bottom nav. */}
                    {!isAdminDomain && !window.location.pathname.startsWith('/m') && (
                      <SafeRoute>
                        <FloatingSupportChat />
                      </SafeRoute>
                    )}
                    {/* Global keyboard shortcuts (/, g h, g s, g t, ?) */}
                    <SafeRoute>
                      <KeyboardShortcuts />
                    </SafeRoute>
                    {/* Global Cmd/Ctrl-K search palette */}
                    <SafeRoute>
                      <SearchPalette />
                    </SafeRoute>
                    {/* Toast notifications - rendered at root level */}
                    <ToastContainer />
                    <QuickStartModalHost />
                  </div>
                      </QuickStartAssistantProvider>
                      </QuickStartProvider>
                      </ChatSupportProvider>
                      </ProductTourProvider>
                      </WorkflowViewProvider>
                    </Router>
                  </DocumentProvider>
                </ActivityTracker>
              </ImpersonationProvider>
            </BackgroundReportProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;